import styles from './TabsModule.scss';

import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import React from 'react';
import Slider from 'react-slick';

import { CardAssembly, ContentType, TabItem as TabItemType, TabsAssembly, is } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Entries } from 'src/api/contentful/utilTypes';
import { getField } from 'src/utils/EntryUtil';

import { ArrowButton as CustomArrow, ArrowType } from '../ArrowButton/ArrowButton';
import { Text } from '../Contentful/Text';
import { withBrowser } from '../Responsive/WithBrowser';
import { CustomDot } from './CustomDot';
import { TabItem } from './TabItem';

interface TabsModuleState {
  currentSlideIndex: number;
}

interface TabsModuleProps extends React.AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<TabsAssembly>;
  isSmallerThanDesktop: boolean;
  makeTabsPanelSliding?: boolean;
}

class TabsModule extends React.Component<TabsModuleProps> {
  static defaultProps = { makeTabsPanelSliding: true };

  private slider = React.createRef<Slider>();

  private tabsRef = React.createRef<HTMLUListElement>();

  state: TabsModuleState = { currentSlideIndex: 0 };

  componentDidMount() {
    const tabsPanel = this.tabsRef.current;

    if (!this.props.makeTabsPanelSliding) {
      // this determines how much the tabs panel needs to shift to the right when it first renders so that the first child is in the middle
      if (this.props.isSmallerThanDesktop && tabsPanel && (tabsPanel.firstChild instanceof HTMLElement)) {
        const elementWidth = tabsPanel.firstChild.clientWidth;
        tabsPanel.style.left = `calc(50vw - ${elementWidth / 2}px)`;
      }
    }
  }

  render() {
    const { currentSlideIndex } = this.state;
    const { entry, isSmallerThanDesktop, className, makeTabsPanelSliding, ...otherProps } = this.props;

    if (!entry || !entry.fields) return null;

    const { items, backgroundColor } = entry.fields;

    if (!items || items.length < 2) {
      console.warn('There should be at least 2 modules');
      return null;
    }

    return (
      <section className={cx(styles.tabsModuleWrapper, className)} style={{ backgroundColor }} {...otherProps}>
        <div className={styles.container}>
          <Text tagName="h2" field="title" className={styles.title} entry={entry} />
          <div className={styles.sliderWrapper}>
            <Slider
              ref={this.slider}
              className={styles.slider}
              {...this.getSliderProps(items, currentSlideIndex, isSmallerThanDesktop)}
            >
              {items.map(item => <TabItem key={item.sys.id} item={item} />)}
            </Slider>
          </div>
        </div>
      </section>
    );
  }

  handleSliding = (newCurrentSlideIndex: number) => {
    const oldCurrentSlideIndex = this.state.currentSlideIndex;

    if (!this.slider.current || newCurrentSlideIndex === oldCurrentSlideIndex) return null;

    this.slider.current.slickGoTo(newCurrentSlideIndex, false);

    return this.setState({ currentSlideIndex: newCurrentSlideIndex });
  };

  private getSliderProps = (items: Entries<TabItemType>, currentSlideIndex: number, isSmallerThanDesktop: boolean) => {
    const hideArrowIfCardAssembly = is<CardAssembly>(ContentType.CARD_ASSEMBLY, getField(items[currentSlideIndex], 'module'));

    const { makeTabsPanelSliding } = this.props;

    return {
      dots: true,
      speed: 300,
      infinite: false,
      dotsClass: cx(styles.tabsPanelContainer, { [styles.tabsPanelContainerScrollable]: makeTabsPanelSliding }),
      slidesToShow: 1,
      slidesToScroll: 1,
      appendDots: this.appendDots,
      beforeChange: this.beforeChange,
      customPaging: (index: number) => {
        const title = getField(items[index], 'title');
        // I had to create a separate handler for sliding because the slides weren't sliding when given a custom dot component
        return (
          <CustomDot
            title={title}
            currentSlideIndex={currentSlideIndex}
            slideIndex={index}
            handleSliding={this.handleSliding}
            ULRef={isSmallerThanDesktop ? this.tabsRef : undefined}
            makeTabsPanelSliding={makeTabsPanelSliding!}
          />
        );
      },
      prevArrow: isSmallerThanDesktop && currentSlideIndex !== 0 && !hideArrowIfCardAssembly ?
        <CustomArrow arrowDirection={ArrowType.PREV} classNameOverride={styles.previousArrow} /> :
        undefined,
      nextArrow: isSmallerThanDesktop && currentSlideIndex !== items.length - 1 && !hideArrowIfCardAssembly ?
        <CustomArrow arrowDirection={ArrowType.NEXT} classNameOverride={styles.nextArrow} /> :
        undefined,
    };
  };

  // Since afterChange is not triggered when adaptiveHeight is true, using beforeChange as a workaround
  private beforeChange = (current: number, next: number) => {
    setTimeout(() => this.setState(prevState => ({ ...prevState, currentSlideIndex: next })), 0);
  };

  private appendDots = (dots: React.ReactNode) => (
    <div>
      <ul ref={this.tabsRef} className={styles.tabsPanel}>{dots}</ul>
    </div>
  );
}

const Browsered = withBrowser(browser => ({ isSmallerThanDesktop: browser.lessThan.desktop }))(TabsModule);

export {
  TabsModule as _InternalTabsModule,
  Browsered as TabsModule,
  TabsModuleProps,
};
